import type { PurchaseDetailsReview } from '@shared/domain/review';
import type { LegalCheckFlag } from '../../legal-check/types';
import { Button } from '@mui/material';
import { ReviewStep } from '@shared/domain/review';
import React from 'react';
import { PURCHASE_DETAILS_FORM_FIELDS } from '../../data';
import { getReviewStepName } from '../../helpers/ReviewStepNames';
import { useReviewForm } from '../../hooks/useReviewForm';
import { QuestionRenderer } from '../QuestionRenderer';
import '../../styles/review-form.scss';

interface PurchaseDetailsReviewProps {
  submitReviewForm?: (formFields: PurchaseDetailsReview, reviewStep: ReviewStep, isDraft?: boolean) => void;
  editMode?: boolean;
  defaultValues?: Partial<PurchaseDetailsReview> & { id?: number };
  handleEditSuccess?: () => void;
  seedLegalCheckFlags?: Record<string, LegalCheckFlag[]>;
}

function PurchaseDetailsReviewForm(props: PurchaseDetailsReviewProps) {
  const { submitReviewForm, editMode, defaultValues, handleEditSuccess, seedLegalCheckFlags } = props;

  const {
    formFields,
    extendFormFields,
    setFormFields,
    errors,
    validateField,
    handleFormFieldChange,
    handleSubmit,
    handleEdit,
    getLegalCheckBindings,
  } = useReviewForm<PurchaseDetailsReview>({
    reviewStep: ReviewStep.PURCHASE_DETAILS,
    formFields: PURCHASE_DETAILS_FORM_FIELDS,
    editMode,
    defaultValues,
    onSubmit: submitReviewForm,
    onEditSuccess: handleEditSuccess,
    seedLegalCheckFlags,
  });

  return (
    <div className="review-page">
      <div data-testid="purchase-details-review-step-content-anchor" />

      <header className="review-page__header">
        <p className="review-page__eyebrow">Step 3 · Purchase details</p>
        <h1 className="review-page__title">
          {editMode ? 'Edit the purchase details' : 'Who bought, and how was the room?'}
        </h1>
        <p className="review-page__subtitle">
          The champion, the detractors, the budget, the cycle. Use titles instead of names so nothing here can be traced
          back.
        </p>
      </header>

      <form>
        <section className="review-section">
          <QuestionRenderer
            questions={PURCHASE_DETAILS_FORM_FIELDS}
            formFields={formFields}
            errors={errors}
            handleFormFieldChange={handleFormFieldChange}
            extendFormFields={extendFormFields}
            validateField={validateField}
            setFormFields={setFormFields}
            getLegalCheckBindings={getLegalCheckBindings}
          />
        </section>
      </form>

      <div className="review-save-bar">
        <Button
          variant="contained"
          className="review-save-bar__button"
          data-testid="review-save-button"
          onClick={editMode ? handleEdit : handleSubmit}
        >
          {editMode ? `Save ${getReviewStepName(ReviewStep.PURCHASE_DETAILS)} changes` : 'Continue to Evaluation'}
        </Button>
      </div>
    </div>
  );
}

export default PurchaseDetailsReviewForm;
